import classNames from 'classnames';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import { FocusContainer } from '@eika/focus-container';

import { ModalDialogProps } from './ModalDialogTypes';
import ModalDialogHeader from './components/ModalDialogHeader';
import PortalWrapper from './components/PortalWrapper';
import { ModalDialogContext, useModalDialogContext } from './contexts';
import { useDelayUnmount, useModalRefs, useModalTracking } from './hooks/useModal';
import useModalScroll from './hooks/useModalScroll';
import getModalDialogClassNames from './util/getModalDialogClassNames';
import mapDeprecatedProps from './util/mapDeprecatedProps';

import './Modal.scss';

const ModalDialog = forwardRef<HTMLDivElement, ModalDialogProps>(({ children, ...props }, forwardedRef) => {
    const {
        id,
        appNamespace,
        onClose,
        title,
        description,
        className,
        contentClassName,
        headerClassName,
        footerClassName,
        footerContent,
        hideCloseButton,
        timeOut,
        onCloseDownSwipe,
        growWithContent = true,
        shouldCloseOnOverlayClick = false,
        shouldExitOnEscape = true,
        borderForHeaderAndFooter = true,
        contentShouldStretch = false,
        size,
        type,
        open,
        headerContent,
        containerClassName,
        animateAs,
        dataTrackName,
        desktopMinHeight: height,
        disableEscapeKeydownOnHiddenCloseButton = false,
        ...rest
    } = mapDeprecatedProps(props);

    const { modalRef, contentRef, portalRef, mounted } = useModalRefs();

    const [mountModal, setMountModal] = useState(false);
    const [modalFinishedOpening, setModalFinishedOpening] = useState(false);
    const [internalOpen, setInternalOpen] = useState(open);
    const [trackOpen, trackClose] = useModalTracking({ dataTrackName, appNamespace, title, id });

    // Used when a submodal is opened by default and there is no transition
    const isReducedMotion = window.matchMedia('prefers-reduced-motion: reduce').matches;
    const mount = useDelayUnmount(isReducedMotion, mounted.current, 300);

    const enterFocus = isReducedMotion
        ? modalFinishedOpening || modalFinishedOpening !== mount
        : modalFinishedOpening && mounted.current;

    const { restoreScrollPosition, cleanUpDisabledBodyScroll } = useModalScroll(contentRef, internalOpen);

    const { contextNamespace, contextType, contextSize, contextSwipe, contextGrowWithContent, contextPortalElement } =
        useModalDialogContext(appNamespace, type, size, onCloseDownSwipe, growWithContent);

    useEffect(() => {
        setInternalOpen(open);
    }, [open, setInternalOpen]);

    useEffect(() => {
        if (internalOpen) {
            setMountModal(true);
            trackOpen();
        }
    }, [setMountModal, internalOpen, trackOpen]);

    const handleClose = useCallback(() => {
        restoreScrollPosition();
        if (contentRef.current) {
            cleanUpDisabledBodyScroll(contentRef.current);
        }
        setInternalOpen(false);
        trackClose();
    }, [restoreScrollPosition, cleanUpDisabledBodyScroll, setInternalOpen, contentRef, trackClose]);

    const onExitFocus = useCallback(() => {
        if (mounted && shouldExitOnEscape) {
            setInternalOpen(false);
        }
    }, [mounted, shouldExitOnEscape, setInternalOpen]);

    const overlayClick = useCallback(() => {
        if (shouldCloseOnOverlayClick) {
            handleClose();
        }
    }, [shouldCloseOnOverlayClick, handleClose]);

    const isSubmodal = contextType === 'sub' || !portalRef.current;
    const isMainModalWithAnimateAsSub = !isSubmodal && animateAs === 'sub';

    const useSwipeDown = contextSwipe && (!isSubmodal || animateAs === 'standard') && !isMainModalWithAnimateAsSub;
    const useSwipeRight = (isSubmodal || isMainModalWithAnimateAsSub) && contextSwipe;

    const headerProps = {
        id,
        onClose: handleClose,
        headerClassName,
        headerContent,
        hideCloseButton,
        type: animateAs ?? contextType,
        onCloseDownSwipe: useSwipeDown,
        borderForHeaderAndFooter,
    };

    const onTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
        if (e.target === modalRef.current) {
            setModalFinishedOpening(internalOpen);
            if (!internalOpen) {
                cleanUpDisabledBodyScroll(contentRef.current);
                restoreScrollPosition();
                setMountModal(false);
                onClose();
            }
        }
    };

    const handlers = useSwipeable({
        onSwipedRight: () => (useSwipeRight ? onClose() : undefined),
    });

    const contentRefPassThrough = (el: HTMLDivElement) => {
        handlers.ref(el);
        contentRef.current = el;
    };

    if (!open && !mountModal) return null;

    return (
        <ModalDialogContext
            namespace={appNamespace ?? contextNamespace}
            type={type ?? contextType}
            size={contextSize}
            onCloseDownSwipe={onCloseDownSwipe}
            growWithContent={contextGrowWithContent}
            portalElement={contextPortalElement ?? portalRef.current}
        >
            <PortalWrapper portalElement={contextPortalElement}>
                <div
                    className={classNames(contextNamespace, containerClassName, 'sgw-modal-dialog__container', {
                        'is-showing': mountModal && internalOpen,
                        'sgw-modal-dialog__sub-container': isSubmodal,
                        'finished-opening': modalFinishedOpening,
                    })}
                >
                    <div
                        {...rest}
                        id={id}
                        className={classNames(
                            'sgw-modal-dialog',
                            { open: mountModal && internalOpen },
                            { 'small-height': height === 'small' },
                            className,
                            getModalDialogClassNames({
                                size: contextSize,
                                type: contextType,
                                growWithContent: contextGrowWithContent,
                                ...props,
                            }),
                        )}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={title && `${id}-title`}
                        aria-describedby={description && `${id}-description`}
                        ref={(el) => {
                            modalRef.current = el;
                            if (forwardedRef) {
                                if (typeof forwardedRef === 'function') {
                                    forwardedRef(el);
                                } else {
                                    forwardedRef.current = el;
                                }
                            }
                        }}
                        onTransitionEnd={onTransitionEnd}
                    >
                        <FocusContainer
                            className="sgw-modal-dialog__focus-container"
                            entry={enterFocus}
                            onExit={onExitFocus}
                            timeOut={timeOut}
                            elementToFocusFirst={contentRef?.current as HTMLElement | undefined}
                            disableEscapeKeyDown={disableEscapeKeydownOnHiddenCloseButton && hideCloseButton}
                        >
                            <ModalDialogHeader {...headerProps} />

                            <div
                                tabIndex={-1}
                                className={classNames('sgw-modal-dialog__content', contentClassName, {
                                    'stretch-container': contentShouldStretch,
                                })}
                                {...handlers}
                                ref={contentRefPassThrough}
                            >
                                {title && (
                                    <h2 id={`${id}-title`} className="sgw-modal-dialog__header-title">
                                        {title}
                                    </h2>
                                )}

                                {description && (
                                    <p
                                        tabIndex={-1}
                                        id={`${id}-description`}
                                        className="sgw-modal-dialog__header-title"
                                    >
                                        {description}
                                    </p>
                                )}
                                {children}
                            </div>

                            {footerContent && (
                                <div
                                    className={classNames('sgw-modal-dialog__footer', footerClassName, {
                                        'with-border': borderForHeaderAndFooter,
                                    })}
                                >
                                    {footerContent}
                                </div>
                            )}
                            {contextType === 'standard' && <div id="sub-modal-wrapper" ref={portalRef} />}
                        </FocusContainer>
                    </div>
                    {!isSubmodal && (
                        <div
                            className={classNames('sgw-modal-dialog__backdrop', {
                                'is-showing': mountModal && internalOpen,
                            })}
                            tabIndex={-1}
                            onClick={overlayClick}
                            onKeyUp={(event) => {
                                if (event.key === 'Escape' || event.key === 'Esc') {
                                    overlayClick();
                                }
                            }}
                        />
                    )}
                </div>
            </PortalWrapper>
        </ModalDialogContext>
    );
});

export default ModalDialog;
